import { useMemo } from 'react';
import { MaterialReactTable } from 'material-react-table';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import { Box, Grid } from '@mui/material';
import { FluxButton, FluxCard, FluxEmptyState, FluxWafer, FluxIcon } from '@meltwater/flux-web-components-react';
import JiraSearch from './jiraSearch';
import { round } from '@meltwater/flux-web-components';
import styles from './jira.module.css';
import csvDownload from 'json-to-csv-export'
import { saveAs } from 'file-saver';

const SalesforceTicketTable = ({ transformedData, salesforceId }) => {
  const columns = useMemo(
    () => [
      {
        header: 'Ticket',
        accessorKey: 'key',
        size: 120,
        Cell: ({ cell }) => (
          <FluxButton
            tint="primary"
            type="text"
            href={`/jira/${cell.getValue()}`}
          >
            {cell.getValue()}
          </FluxButton>
        ),
      },
      {
        header: 'Summary',
        accessorKey: 'summary',
        size: 300,
      },
      {
        header: 'Assignee',
        accessorKey: 'assignee_emailAddress',
        size: 200,
        Cell: ({ cell }) => cell.getValue() || 'Unassigned',
      },
      {
        header: 'Priority',
        accessorKey: 'priority',
        size: 100,
        Cell: ({ cell }) => (
          <Chip 
            label={cell.getValue()} 
            size="small"
            color={
              cell.getValue() === 'High' ? 'error' :
              cell.getValue() === 'Medium' ? 'warning' : 'default'
            }
          />
        ),
      },
      {
        header: 'Status',
        accessorKey: 'status',
        size: 120,
        Cell: ({ cell }) => (
          <Box
            component="span"
            sx={(theme) => ({
              backgroundColor:
                (cell.getValue() === "Resolved" || cell.getValue() === "Closed")
                  ? theme.palette.success.light
                  : theme.palette.warning.light,
              borderRadius: '0.25rem',
              color: '#fff',
              maxWidth: '9ch',
              p: '0.25rem',
              fontSize: '0.75rem',
              fontWeight: 'bold',
            })}
          >
            {cell.getValue()}
          </Box>
        ),
      },
      {
        header: 'Created (UTC)',
        accessorKey: 'created',
        size: 180,
        Cell: ({ cell }) => {
          const date = new Date(cell.getValue());
          return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}T${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}:${String(date.getUTCSeconds()).padStart(2, '0')}`;
        }
      },
      {
        header: 'Last Update (UTC)',
        accessorKey: 'lastupdate',
        size: 180,
        Cell: ({ cell }) => {
          const date = new Date(cell.getValue());
          return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}T${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}:${String(date.getUTCSeconds()).padStart(2, '0')}`;
        }
      },
    ],
    []
  );

  // Get all tickets from all opportunities
  const allTickets = useMemo(() => {
    if (!transformedData?.opportunities) return [];
    return transformedData.opportunities.flatMap(opp => opp.tickets || []);
  }, [transformedData]);

  const Opheader = (data) => {
    return (<>
      <div style={{ 'marginLeft': 10 }}>
        <Grid container>
          <Grid marginTop={1} item xs={12} >
            <FluxWafer primaryText={data.companyname} secondaryText={`${data.support_level || ""} Mrr: ${round(data.mrr || 0)} ${data.churned ? '(Churned)' : '(Active)'}`}>
              {data.churned && <FluxIcon name={'general:bolt'} slot='prefix' className={styles.statusfluxicon} bgColor='var(--flux-color-red)'></FluxIcon>}
              {!data.churned && <FluxIcon name={'general:blur'} slot='prefix' className={styles.statusfluxicon} bgColor='var(--flux-color-green)'></FluxIcon>}
            </FluxWafer>
          </Grid>
        </Grid>
      </div>
    </>
    )
  }

  const handleExportRows = (rows, filename = 'salesforce-tickets') => {
    const rowData = rows.map((row) => row.original);
    const dataToConvert = {
      data: rowData,
      filename: `${filename}-${new Date().toISOString().split('T')[0]}`,
      delimiter: ',',
      headers: ['key', 'summary', 'assignee_emailAddress', 'priority', 'status', 'created']
    };
    csvDownload(dataToConvert);
  };

  const exportToWord = (rows, filename = 'salesforce-tickets') => {
    const rowData = rows.map((row) => row.original);
    
    let wordContent = `
      <html>
        <head>
          <meta charset="utf-8">
          <title>Salesforce Tickets Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            table { border-collapse: collapse; width: 100%; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .header { background-color: #0066cc; color: white; padding: 20px; margin-bottom: 20px; }
            .summary { margin-bottom: 20px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Salesforce Tickets Report</h1>
            <p>Salesforce ID: ${salesforceId}</p>
            <p>Generated: ${new Date().toLocaleString()}</p>
          </div>
          <div class="summary">
            <h2>Summary</h2>
            <p>Total Tickets: ${rowData.length}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>Ticket</th>
                <th>Summary</th>
                <th>Assignee</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
    `;
    
    rowData.forEach((row) => {
      wordContent += `
        <tr>
          <td>${row.key}</td>
          <td>${row.summary}</td>
          <td>${row.assignee_emailAddress || 'Unassigned'}</td>
          <td>${row.priority}</td>
          <td>${row.status}</td>
          <td>${new Date(row.created).toLocaleDateString()}</td>
        </tr>
      `;
    });
    
    wordContent += `
            </tbody>
          </table>
        </body>
      </html>
    `;
    
    const blob = new Blob([wordContent], { type: 'application/msword' });
    saveAs(blob, `${filename}-${new Date().toISOString().split('T')[0]}.doc`);
  };

  if (!transformedData?.opportunities || transformedData.opportunities.length === 0) {
    return (
      <div style={{ margin: '20px' }}>
        <FluxCard>
          <FluxEmptyState style={{ minHeight: '400px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div slot="title" style={{ marginBottom: '2rem' }}>No tickets found for Salesforce ID: {salesforceId}</div>
            <div slot="actions" style={{ position: 'relative', zIndex: 1 }}>
              <JiraSearch />
            </div>
          </FluxEmptyState>
        </FluxCard>
      </div>
    );
  }

  return (
    <div style={{ margin: '20px' }}>
      <FluxCard style={{ marginBottom: '20px' }}>
        <Opheader data={transformedData} />
      </FluxCard>
      {/* Summary Header */}
      <Box mb={3}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box>
                <strong>Total Tickets:</strong> {allTickets.length}
              </Box>
              <Box>
                <strong>Salesforce ID:</strong> {salesforceId}
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <JiraSearch />
          </Grid>
        </Grid>
      </Box>

      {/* Single Table for All Tickets */}
      <MaterialReactTable
        columns={columns}
        data={allTickets}
        density="compact"
        enableStickyHeader
        layoutMode="grid"
        enableColumnFilterModes={true}
        enableGrouping={true}
        enableColumnPinning={true}
        enableFacetedValues={true}
        enableRowSelection={true}
        enableGlobalFilter={true}
        enablePagination={true}
        initialState={{ 
          density: 'compact',  
          pagination: { pageSize: 10, pageIndex: 0 },
          sorting: [{ id: 'created', desc: true }] // Sort by created date, newest first
        }}
        muiPaginationProps={{
          color: 'primary',
          shape: 'rounded',
          variant: 'outlined',
        }}
        paginationDisplayMode="pages"
        renderTopToolbarCustomActions={({ table }) => (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <FluxButton
              tint="primary"
              type="filled"
              onClick={() => handleExportRows(
                table.getPrePaginationRowModel().rows, 
                `salesforce-${salesforceId}`
              )}
            >
              Export All Data
            </FluxButton>
            <FluxButton
              tint="accent"
              type="filled"
              onClick={() => handleExportRows(
                table.getSelectedRowModel().rows, 
                `salesforce-${salesforceId}-selected`
              )}
              disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
            >
              Export Selected
            </FluxButton>
            <FluxButton
              tint="accent"
              type="filled"
              onClick={() => exportToWord(
                !table.getIsSomeRowsSelected() 
                  ? table.getPrePaginationRowModel().rows 
                  : table.getSelectedRowModel().rows,
                `salesforce-${salesforceId}`
              )}
            >
              Export Word
            </FluxButton>
          </Box>
        )}
      />
    </div>
  );
};

export default SalesforceTicketTable;